'use client';

import React, { useState, useRef, useEffect, forwardRef } from 'react';
import classnames from 'classnames';

import Typography from '../Typography/Typography';
import Iconography from '../Iconography/Iconography';
import Box from '../Box/Box';

import { dataLayerHandler } from '../../../utils/analytics';
import { COUNTRY_CODES } from './PhoneInput.constant';
import {
  getExampleNumber,
  AsYouType,
  validatePhoneNumberLength,
  parsePhoneNumber,
} from 'libphonenumber-js';
import type { CountryCode as LibCountryCode } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

import styles from './PhoneInput.module.scss';

// Type definitions
type Theme = 'dark' | 'light';
type Size = 'small' | 'medium' | 'large';
type Direction = 'ltr' | 'rtl';
type CountryCode = 'AE' | 'CA' | 'CH' | 'DE' | 'NL' | 'NO' | 'SA' | 'US';
type ValidationType = 'success' | 'warning' | 'error';

interface ValidationResponse {
  validationType?: ValidationType;
  message?: string;
}

interface CountryOption {
  countryCode: string;
  label: string;
  phoneCode: string;
}

export interface PhoneInputProps {
  theme?: Theme;
  size?: Size;
  name?: string;
  register?: (name: string) => Record<string, unknown>;
  label?: string;
  helperText?: string;
  selectedOption?: CountryCode;
  direction?: Direction;
  /**
   * Validation response object containing error/warning/success state and message.
   */
  validationResponse?: ValidationResponse;
  /**
   * Whether to show the validation message area.
   * @default true
   */
  showValidation?: boolean;
  gaComponent?: string;
  required?: boolean;
  disabled?: boolean;
  inline?: boolean;
  /**
   * Initial value in E.164 format or national format
   */
  value?: string;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      theme = 'dark',
      size = 'medium',
      name,
      register,
      label,
      helperText,
      selectedOption = 'US',
      direction = 'ltr',
      validationResponse,
      showValidation = true,
      gaComponent = 'phoneInput',
      required = false,
      disabled,
      inline = false,
      value,
    },
    ref
  ) => {
    const [optionSelected, setOptionSelected] = useState<CountryOption | undefined>();
    const [placeholder, setPlaceholder] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [e164Value, setE164Value] = useState<string>('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const dropdownButtonRef = useRef<HTMLDivElement>(null);
    const dropdownMenuRef = useRef<HTMLUListElement>(null);
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    // Get register props and extract onChange to call it manually
    const registerProps = register ? register(name || 'phone') : {};
    const registerOnChange = registerProps.onChange as
      | ((e: React.ChangeEvent<HTMLInputElement>) => void)
      | undefined;

    const handleMenuItemClick = (option: CountryOption) => {
      setIsMenuOpen(false);
      setOptionSelected(option);
      dataLayerHandler({
        interactionType: 'dropdown',
        gaComponent,
        label,
      });
    };

    const handleButtonClick = () => {
      if (!disabled) {
        setIsMenuOpen((prevState) => !prevState);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' && !disabled) {
        setIsMenuOpen((prevState) => !prevState);
      }
    };

    const handleDocumentClick = (e: MouseEvent) => {
      if (
        !dropdownButtonRef?.current?.contains(e.target as Node) &&
        !dropdownMenuRef?.current?.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    /**
     * Converts a phone number to E.164 format
     * @param phoneValue - The phone number to convert
     * @param countryCode - The country code
     * @returns The E.164 formatted number or empty string if invalid
     */
    const toE164Format = (phoneValue: string, countryCode: string): string => {
      if (!phoneValue || !countryCode) return '';
      try {
        const parsed = parsePhoneNumber(phoneValue, countryCode as LibCountryCode);
        if (parsed) {
          return parsed.format('E.164');
        }
      } catch {
        // Invalid phone number, return empty string
      }
      return '';
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const phoneFormat = new AsYouType(optionSelected?.countryCode as CountryCode);
      const inputValue = e.target.value;
      const validLength = validatePhoneNumberLength(
        inputValue,
        optionSelected?.countryCode as CountryCode
      );
      const nativeEvent = e.nativeEvent as InputEvent;

      let newPhoneNumber = inputValue;

      if (nativeEvent.inputType === 'deleteContentBackward') {
        newPhoneNumber = inputValue;
      } else if (validLength !== 'TOO_LONG') {
        newPhoneNumber = phoneFormat.input(inputValue);
      } else {
        // Don't update if too long
        return;
      }

      setPhoneNumber(newPhoneNumber);

      // Update E.164 value for form submission
      if (optionSelected?.countryCode) {
        const newE164Value = toE164Format(newPhoneNumber, optionSelected.countryCode);
        setE164Value(newE164Value);
      }
    };

    // Initialize phone number from value prop
    useEffect(() => {
      if (value && optionSelected?.countryCode) {
        try {
          const parsed = parsePhoneNumber(value, optionSelected.countryCode as LibCountryCode);
          if (parsed) {
            setPhoneNumber(parsed.formatNational());
            setE164Value(parsed.format('E.164'));
          }
        } catch {
          // If parsing fails, use value directly
          setPhoneNumber(value);
          setE164Value(value);
        }
      }
    }, [value, optionSelected?.countryCode]);

    // When E.164 value changes, notify react-hook-form by calling register's onChange
    useEffect(() => {
      if (hiddenInputRef.current && registerOnChange) {
        // Create a synthetic event with the E.164 value
        const syntheticEvent = {
          target: {
            name: name || 'phone',
            value: e164Value,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        registerOnChange(syntheticEvent);
      }
    }, [e164Value, registerOnChange, name]);

    useEffect(() => {
      document.addEventListener('click', handleDocumentClick as EventListener);

      return () => {
        document.removeEventListener('click', handleDocumentClick as EventListener);
      };
    }, []);

    useEffect(() => {
      if (selectedOption) {
        setOptionSelected(() => {
          let activeOption: CountryOption | undefined;
          for (let i = 0; i < COUNTRY_CODES.length; i++) {
            if (COUNTRY_CODES[i]?.countryCode === selectedOption) {
              activeOption = COUNTRY_CODES[i];
              break;
            }
          }
          return activeOption;
        });
      }
    }, [selectedOption]);

    useEffect(() => {
      if (optionSelected) {
        const exampleNumber = getExampleNumber(
          optionSelected?.countryCode as CountryCode,
          examples
        )?.nationalNumber;
        if (exampleNumber) {
          const formattedPlaceholder = parsePhoneNumber(
            exampleNumber,
            optionSelected?.countryCode as CountryCode
          ).formatNational();
          setPlaceholder(formattedPlaceholder);
        }
        // Only clear phone number if there's no value provided
        if (!value) {
          setPhoneNumber('');
        }
      }
    }, [optionSelected, value]);

    const constructOptionElement = (option: CountryOption, index: string) => {
      return (
        <li
          key={index}
          tabIndex={0}
          className={classnames(styles.menuItem, styles[direction], {
            [styles.active]: optionSelected?.countryCode === option?.countryCode,
          })}
          role="option"
          aria-selected={optionSelected?.countryCode === option?.countryCode}
          onClick={() => handleMenuItemClick(option)}
        >
          <Typography
            className={classnames(styles.menuItemLabel, {
              [styles.selected]: optionSelected?.countryCode === option?.countryCode,
            })}
            variant="body"
            size={size}
            direction={direction}
          >
            {option?.label}
          </Typography>
          <Box className={styles.phoneCode} pl={4} direction={direction}>
            <Typography
              className={classnames(styles.menuItemLabel, {
                [styles.selected]: optionSelected?.countryCode === option?.countryCode,
              })}
              variant="body"
              size={size}
              direction={direction}
            >
              ({option?.phoneCode})
            </Typography>
          </Box>
        </li>
      );
    };

    return (
      <Box
        className={classnames(
          styles.phoneInputContainer,
          styles[`theme_${theme}`],
          styles[`size_${size}`],
          {
            [styles[`validation-${validationResponse?.validationType}`]]:
              validationResponse?.validationType,
          }
        )}
      >
        {(label || helperText) && (
          <Box mb={4} dir={direction}>
            {label && (
              <label htmlFor={name}>
                <Typography
                  className={styles.label}
                  variant="label"
                  size="small"
                  direction={direction}
                  theme={theme}
                  color="gray-3"
                >
                  {label}
                  {required && '*'}
                </Typography>
              </label>
            )}
            {helperText && (
              <Typography
                className={styles.helperText}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                color="gray-5"
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}
        <Box className={classnames(styles.container, styles[direction])}>
          <Box
            ref={dropdownButtonRef}
            className={classnames(styles.dropdownMenuButton, styles[direction], {
              [styles.disabled]: disabled,
              [styles.noOutline]: inline,
            })}
            onClick={handleButtonClick}
            onKeyDown={handleKeyDown}
            aria-expanded={isMenuOpen}
            role="button"
            tabIndex={0}
            aria-haspopup="listbox"
            aria-label={placeholder}
          >
            <Typography
              className={classnames(styles.selectedOption, {
                [styles.placeholder]: !optionSelected,
                [styles.disabled]: disabled,
              })}
              variant="body"
              size={size}
              theme={theme}
              color={optionSelected ? 'white' : disabled ? 'gray-5' : 'gray-3'}
              direction={direction}
            >
              {optionSelected?.phoneCode}
            </Typography>
            <Box pl={4}>
              <Iconography
                className={classnames({
                  [styles.dropdownIcon]: isMenuOpen,
                })}
                category="arrows"
                name="chevron-down"
                theme={theme}
                color="white"
                size={{ xs: 16, md: 20, lg: 24 }}
              />
            </Box>
          </Box>
          {/* Hidden input stores E.164 value for form submission */}
          <input
            ref={(el) => {
              hiddenInputRef.current = el;
              // Also set the register ref if provided
              const registerRef = registerProps.ref as
                | React.RefCallback<HTMLInputElement>
                | undefined;
              if (registerRef) registerRef(el);
            }}
            type="hidden"
            name={name}
            value={e164Value}
            onChange={() => {}} // Handled via useEffect
          />
          {/* Visible input shows formatted display value */}
          <input
            ref={ref}
            className={classnames(
              styles.phoneInput,
              styles[size],
              styles[`direction-${direction}`]
            )}
            type="tel"
            placeholder={placeholder}
            value={phoneNumber}
            required={required}
            id={name}
            data-phone-input="true"
            aria-label={placeholder}
            onChange={handleChange}
            autoComplete="tel-national"
            aria-describedby={name ? `${name}-validation-message` : undefined}
          />
          <Iconography
            category="global"
            name="phone"
            color={theme === 'dark' ? 'white' : 'black'}
          />
        </Box>
        {isMenuOpen && (
          <ul ref={dropdownMenuRef} className={styles.dropdownMenu} role="listbox">
            {COUNTRY_CODES.map((option: CountryOption, index: number) =>
              constructOptionElement(option, `option_${index}`)
            )}
          </ul>
        )}
        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={classnames(styles.validationMessage)}
                variant="body"
                size="extraSmall"
                direction={direction}
                theme={theme}
                id={name ? `${name}-validation-message` : undefined}
              >
                {validationResponse.message || ''}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
